import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl, baseHeaders, storeId } from '@constants'
import { draftUpdater, draftUpdaterMerge } from '@src/utils'
import { prepareHeaders, transformResponse } from '@src/reduxjs'

const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Users', 'UserPhotos'],
  endpoints: (builder) => ({
    get: builder.query({
      query: ({ search, page, perPage, role, permissions }) => {
        const params = {}

        if (page) {
          params.page = page
        }

        if (perPage) {
          params.per_page = perPage
        }

        if (role) {
          params.role = role
        }

        if (search) {
          params.search = search
        }

        if (permissions && permissions.length) {
          permissions.forEach((permission, index) => {
            params[`permissions[${index}]`] = permission
          })
        }

        return { url: 'users', params }
      },
      providesTags: ['Users'],
    }),
    getPhotos: builder.query({
      query: ({ page, perPage, user = {} }) => {
        const params = {}

        if (page) {
          params.page = page
        }

        if (perPage) {
          params.per_page = perPage
        }

        return { url: `users/${user.id}/photos`, params }
      },
      transformResponse,
      providesTags: ['UserPhotos'],
    }),
    add: builder.mutation({
      query: (user) => ({ url: 'users', method: 'POST', body: { ...user, store_id: storeId } }),
      invalidatesTags: (results, error, { id }) => {
        return ['Users']
      },
    }),
    getOne: builder.query({
      query: ({ user }) => {
        return {
          url: `users/${user.id}`,
          method: 'GET',
        }
      },
      async onQueryStarted({ user, getQueryParams }, { dispatch, queryFulfilled }) {
        dispatch(usersApi.util.updateQueryData('get', getQueryParams, draftUpdater({ ...user, isUpdating: true })))

        try {
          const { data } = await queryFulfilled

          if (data) {
            dispatch(usersApi.util.updateQueryData('get', getQueryParams, draftUpdater(data.data)))
          }
        } catch {
          dispatch(usersApi.util.invalidateTags(['Users']))
        }
      },
    }),
    update: builder.mutation({
      query: ({ user }) => {
        return {
          url: `users/${user.id}`,
          method: 'PATCH',
          body: user,
        }
      },
      async onQueryStarted({ user, ...patch }, { dispatch, queryFulfilled }) {
        dispatch(usersApi.util.updateQueryData('get', patch.getQueryParams, draftUpdaterMerge({ ...user, isDirty: false })))

        try {
          await queryFulfilled
        } catch {
          dispatch(usersApi.util.invalidateTags(['Users']))
        }
      },
    }),
    delete: builder.mutation({
      query: (user) => ({ url: `users/${user.id}`, method: 'DELETE' }),
      invalidatesTags: (results, error, { id }) => {
        return ['Users']
      },
    }),
  }),
})

export const { useGetOneQuery, useGetQuery, useAddMutation, useDeleteMutation, useUpdateMutation, useGetPhotosQuery } = usersApi

export default usersApi
